/**
 * iOS Keyboard Fix Utility
 * Handles iOS Safari keyboard open/close events to prevent layout issues
 */

let visualViewport: VisualViewport | null | undefined;
let isKeyboardOpen = false;

/**
 * Initialize iOS keyboard fix
 * Should be called once when the app starts
 */
export const initializeIOSKeyboardFix = () => {
  // Only run on iOS devices
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  if (!isIOS) return;

  // Check if Visual Viewport API is supported
  if ('visualViewport' in window) {
    visualViewport = window.visualViewport;

    // Store the original viewport height
    const originalHeight = visualViewport?.height || window.innerHeight;

    const handleViewportChange = () => {
      if (!visualViewport) return;

      const currentHeight = visualViewport.height;
      const heightDifference = originalHeight - currentHeight;

      // If viewport height decreased by more than 150px, keyboard is likely open
      if (heightDifference > 150) {
        if (!isKeyboardOpen) {
          isKeyboardOpen = true;
          document.body.classList.add('keyboard-open');

          // Fix viewport by ensuring proper scroll position
          setTimeout(() => {
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
              activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 300);
        }
      } else {
        // Keyboard is closed
        if (isKeyboardOpen) {
          isKeyboardOpen = false;
          document.body.classList.remove('keyboard-open');

          // Force repaint to fix gray area issue
          setTimeout(() => {
            window.scrollTo(window.scrollX, window.scrollY + 1);
            window.scrollTo(window.scrollX, window.scrollY - 1);
          }, 100);
        }
      }
    };

    // Listen to viewport changes
    visualViewport?.addEventListener('resize', handleViewportChange);
    visualViewport?.addEventListener('scroll', handleViewportChange);
  }

  // Fallback for older iOS versions without Visual Viewport API
  let previousHeight = window.innerHeight;

  window.addEventListener('resize', () => {
    const currentHeight = window.innerHeight;
    const heightDifference = previousHeight - currentHeight;

    if (heightDifference > 150) {
      document.body.classList.add('keyboard-open');
    } else if (heightDifference < -50) {
      document.body.classList.remove('keyboard-open');

      // Fix gray area
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }

    previousHeight = currentHeight;
  });

  // Handle input focus/blur events
  const inputs = document.querySelectorAll('input, textarea, select');
  inputs.forEach(input => {
    input.addEventListener('focus', () => {
      document.body.classList.add('keyboard-open');
    });

    input.addEventListener('blur', () => {
      // Delay to check if another input was focused
      setTimeout(() => {
        const activeElement = document.activeElement;
        if (activeElement?.tagName !== 'INPUT' &&
            activeElement?.tagName !== 'TEXTAREA' &&
            activeElement?.tagName !== 'SELECT') {
          document.body.classList.remove('keyboard-open');
        }
      }, 100);
    });
  });
};

/**
 * Force a viewport refresh to fix gray area issues
 */
export const forceViewportRefresh = () => {
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  if (!isIOS) return;

  // Force a repaint by scrolling slightly
  const currentScroll = window.scrollY;
  window.scrollTo(0, currentScroll + 1);
  requestAnimationFrame(() => {
    window.scrollTo(0, currentScroll);
  });
};

/**
 * Scroll element into view with iOS-safe behavior
 */
export const scrollIntoViewIOS = (element: HTMLElement) => {
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

  if (isIOS) {
    // Use a smoother approach for iOS
    setTimeout(() => {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }, 100);
  } else {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }
};
